"use client"

import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Resources } from "../categoryPanel"
import { ResourceLink } from "./ResourceLink"
import { BsFiletypeDoc, BsFiletypeXls, BsFiletypePdf } from "react-icons/bs"
import { FaTag } from "react-icons/fa6"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { generateSlug } from "@/utils/helpers/utils"

const FILE_THEMES = {
   doc: {
      bg: "/_images/resources/doc.png",
      iconBg: "bg-[#3563C9]",
      icon: BsFiletypeDoc,
   },
   xls: {
      bg: "/_images/resources/xls.png",
      iconBg: "bg-[#5BA321]",
      icon: BsFiletypeXls,
   },
   pdf: {
      bg: "/_images/resources/pdf.png",
      iconBg: "bg-[#D92B2B]",
      icon: BsFiletypePdf,
   },
} as const

const normalizeFileType = (type?: string | null): "doc" | "xls" | "pdf" => {
   switch (type?.toLowerCase()) {
      case "doc":
      case "docx":
         return "doc"
      case "xls":
      case "xlsx":
      case "excel":
         return "xls"
      default:
         return "pdf"
   }
}

interface ResourceTemplateCardProps {
   item: Resources
   href: string
   ariaLabel: string
   categoryName?: string
}

export function ResourceTemplateCard({ item, href, ariaLabel }: ResourceTemplateCardProps) {
   const pathname = usePathname()

   const fileType = normalizeFileType(item.contentType)
   const theme = FILE_THEMES[fileType]

   const activeCategory = item.category?.name ?? "Uncategorized"

   const segments = pathname.split("/")
   const currentResourceType = segments[2] || "library"

   const categoryLink = `/resources/${currentResourceType}/${generateSlug(activeCategory)}`

   return (
      <Card className="group col-span-full flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-0 shadow-sm sm:col-span-1">
         <Link aria-label={ariaLabel} href={href}>
            <div
               className="relative left-0 top-0 m-0 flex h-64 w-full items-center justify-center bg-cover bg-center bg-no-repeat"
               style={{ backgroundImage: `url(${theme.bg})` }}
            >
               <Link
                  aria-label={`View more resources in ${activeCategory}`}
                  href={categoryLink}
                  className="absolute right-4 top-4 flex items-center gap-2 rounded-md border border-gray-100 bg-[#FAF8F7] px-3 py-2 shadow-sm transition-colors hover:bg-custom-primary/20"
               >
                  <FaTag className="h-4 w-4 text-[#EF5929]" aria-hidden="true" />
                  <span className="text-custom-xs-medium uppercase text-[#EF5929]">{activeCategory}</span>
               </Link>
            </div>

            <CardContent className="flex-1 space-y-5 p-6 ">
               <h2 className="line-clamp-2 h-[4.5rem] font-lora text-custom-26-bold ">{item.title}</h2>

               {item.description && (
                  <p className="line-clamp-3 min-h-[4rem] text-custom-base-medium text-[#333333]">{item.description}</p>
               )}
            </CardContent>

            <CardFooter className="p-6 pt-2">
               <ResourceLink href={href} ariaLabel={ariaLabel} />
            </CardFooter>
         </Link>
      </Card>
   )
}
